import { useEffect, useRef, useState } from 'react';

const useCookieSettings = (onCookieSettingsPopupClose?: () => void) => {
  const cookieSettingsOpenButton = useRef<HTMLElement | null>(null);
  const isCookieSettingsPopupDisplayed = useRef(false);
  const [isCookieSettingsAvailable, setIsCookieSettingsAvailable] =
    useState(false);

  const displayCookieSettings = () => {
    cookieSettingsOpenButton.current?.click();
    isCookieSettingsPopupDisplayed.current = true;
  };

  const handleCookieSettingsButton = (buttonElement: HTMLDivElement) => {
    // eslint-disable-next-line no-param-reassign
    buttonElement.style.visibility = 'hidden';
    const openButtonElement = buttonElement.querySelector(
      '.ot-floating-button__open'
    ) as HTMLElement | null;
    cookieSettingsOpenButton.current = openButtonElement;
    setIsCookieSettingsAvailable(true);
  };

  const handleCookieSettingsPopup = (popupElement: HTMLDivElement) => {
    const observer = new MutationObserver(() => {
      // There are some scenarios where the popup element attributes are changed but the display value is still 'none'
      // (some internal behavior that we don't control - e.g. when the initial cookie banner is closed).
      // That's why we hold a ref to manually detect when the popup is open so we can properly run the close handler
      // only when the popup is closed by the user.
      const isPopupClosed =
        isCookieSettingsPopupDisplayed.current &&
        getComputedStyle(popupElement).display === 'none';

      if (isPopupClosed) {
        isCookieSettingsPopupDisplayed.current = false;
        onCookieSettingsPopupClose?.();
      }
    });

    observer.observe(popupElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: false,
    });
  };

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      const addedNodes = mutationsList.reduce<Node[]>(
        (acc, item) => [...acc, ...Array.from(item.addedNodes)],
        []
      );
      const oneTrustParentElement = addedNodes.find(
        (element) => (element as HTMLDivElement).id === 'onetrust-consent-sdk'
      ) as HTMLDivElement;

      if (oneTrustParentElement) {
        const cookieSettingsButtonElement = oneTrustParentElement.querySelector(
          '#ot-sdk-btn-floating'
        ) as HTMLDivElement;
        if (cookieSettingsButtonElement)
          handleCookieSettingsButton(cookieSettingsButtonElement);

        const cookieSettingsPopupElement = oneTrustParentElement.querySelector(
          '#onetrust-pc-sdk'
        ) as HTMLDivElement;
        if (cookieSettingsPopupElement)
          handleCookieSettingsPopup(cookieSettingsPopupElement);

        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      subtree: false,
      childList: true,
    });

    const cookieSettingsButtonElement = document.getElementById(
      'ot-sdk-btn-floating'
    ) as HTMLDivElement;
    if (cookieSettingsButtonElement)
      handleCookieSettingsButton(cookieSettingsButtonElement);

    const cookieSettingsPopupElement = document.getElementById(
      'onetrust-pc-sdk'
    ) as HTMLDivElement;
    if (cookieSettingsPopupElement)
      handleCookieSettingsPopup(cookieSettingsPopupElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isCookieSettingsAvailable, displayCookieSettings };
};

export default useCookieSettings;
